import { TopUpCard } from '../../components/billing/TopUpCard';
import { TokenAlert } from '../../components/billing/TokenAlert';
import { useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCardIcon,
  MapIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { isHostedCheckoutEnabled } from '../../lib/featureFlags';
import { StatusBadge } from '../../components/billing/StatusBadge';
import { UsageBar } from '../../components/billing/UsageBar';
import { PaymentMethodCard } from '../../components/billing/PaymentMethodCard';
import { InvoiceTable } from '../../components/billing/InvoiceTable';
import { TileUsageCard } from '../../components/billing/TileUsageCard';
import { PlanComparisonModal } from '../../components/billing/PlanComparisonModal';
import { AddPaymentModal } from '../../components/billing/AddPaymentModal';
import { BILLING_PERIOD_MONTHS, type Package } from '../../types/subscription';
import { currentMonthOf } from '../../utils/openEndedPlan';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const PAYMENT_DID_NOT_OPEN = 'The payment page did not open. Nothing was charged; please try again.';

export function BillingPlanPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { data: subData, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getCurrentSubscription(),
  });

  // Token figures come from the same endpoint as the dashboard, so every page agrees.
  // (/api/subscriptions/current's `usage` is camelCase and read nothing but zeros here.)
  const { data: usage } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.getMyUsage(),
  });

  const { data: paymentMethods, isLoading: methodsLoading } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => api.getPaymentMethods(),
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.getMyInvoices({ limit: 10 }),
  });

  const { data: packages } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.getPackages(),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.reactivateSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  const changePlanMutation = useMutation({
    mutationFn: (packageId: string) => api.changeSubscription(packageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      setIsPlanModalOpen(false);
    },
  });

  const [buyingPack, setBuyingPack] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);
  const buyPackMutation = useMutation({
    mutationFn: ({ packId, currency }: { packId: string; currency: string }) =>
      api.buyTokenPack((usage?.package ?? pkg)!.id, packId, currency),
    onMutate: ({ packId }) => {
      setBuyingPack(packId);
      setBuyError(null);
    },
    onSuccess: (data) => {
      const url = data.checkout_url ?? data.url;
      if (url) window.location.href = url;
      else setBuyError(PAYMENT_DID_NOT_OPEN);
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setBuyError(err.response?.data?.error ?? PAYMENT_DID_NOT_OPEN);
    },
    onSettled: () => setBuyingPack(null),
  });

  const portalMutation = useMutation({
    mutationFn: () => api.createBillingPortalSession(),
    onSuccess: (data) => {
      window.open(data.url, '_blank');
    },
  });

  const subscription = subData?.subscription;

  const handleSelectPlan = (pkg: Package) => {
    if (subscription?.stripe_subscription_id) {
      changePlanMutation.mutate(pkg.id);
    } else {
      navigate(`/dashboard/checkout?package=${pkg.slug}`);
    }
  };

  const handlePaymentMethodAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
  };
  const isPlanChanging = changePlanMutation.isPending;
  const pkg = subscription?.package || usage?.package;
  const invoices = invoicesData?.data ?? [];

  const quarterlyPrice = pkg ? (pkg.monthly_price_cents * BILLING_PERIOD_MONTHS) / 100 : 0;
  const tokensUsed = usage?.tokens_used ?? 0;
  const tokensIncluded = usage?.tokens_included ?? 0;
  const tokensRolledOver = usage?.tokens_rolled_over ?? 0;
  const tokensRemaining = usage?.tokens_remaining ?? 0;
  const tokensTotal = tokensIncluded + tokensRolledOver;
  // A customer on the token ledger: monthly allowances, no roll-over, and what is owed shown.
  const onLedger = usage?.tokens_topup_remaining !== undefined;
  const runOut = usage?.tokens_run_out;
  // A plan with no end date renews its tokens monthly: show the month, not a period to 9999.
  const thisMonth = subscription ? currentMonthOf(subscription.current_period_start, subscription.current_period_end) : null;
  const daysRemaining = subscription?.current_period_end
    ? Math.max(0, differenceInCalendarDays(thisMonth?.end ?? new Date(subscription.current_period_end), new Date()))
    : 0;
  const deviceCount = usage?.device_count ?? 0;

  if (subLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Billing & Plan</h1>
        <p className="text-slate-400">
          Manage your subscription, view usage, and update payment details.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Plan & Usage */}
        <div className="lg:col-span-2 space-y-8">
          {/* Section A: Plan Overview */}
          {subscription ? (
            <section className="card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl -mr-20 -mt-20" />

              <div className="relative z-10">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pkg?.name || 'Subscription'}
                      </h2>
                      <StatusBadge status={subscription.status as any} />
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">
                        ${quarterlyPrice.toFixed(0)}
                      </span>
                      <span className="text-slate-400">NZD / quarter</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <button
                      onClick={() => setIsPlanModalOpen(true)}
                      className="btn-primary w-full sm:w-auto"
                    >
                      Change Plan
                    </button>
                    {subscription.cancel_at_period_end ? (
                      <button
                        onClick={() => reactivateMutation.mutate()}
                        disabled={reactivateMutation.isPending}
                        className="w-full sm:w-auto bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 font-semibold py-2 px-4 rounded-lg border border-amber-500/30 transition-colors duration-200 disabled:opacity-50"
                      >
                        {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate Subscription'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsCancelModalOpen(true)}
                        className="w-full sm:w-auto bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold py-2 px-4 rounded-lg border border-red-500/30 transition-colors duration-200"
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                </div>

                {subscription.cancel_at_period_end && (
                  <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-6">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-400">
                      Your subscription will cancel at the end of the current billing period
                      on {formatDate(subscription.current_period_end)}. You can reactivate
                      at any time before then.
                    </p>
                  </div>
                )}

                <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-300 mb-1">
                      {thisMonth ? 'This month' : 'Current Billing Period'}
                    </p>
                    <p className="text-white">
                      {thisMonth
                        ? `${formatDate(thisMonth.start.toISOString())} – ${formatDate(thisMonth.end.toISOString())}`
                        : `${formatDate(subscription.current_period_start)} – ${formatDate(subscription.current_period_end)}`}
                    </p>
                    {thisMonth && <p className="text-sm text-slate-400 mt-1">No end date</p>}
                  </div>
                  <div className="sm:text-right">
                    <p className="text-sm font-medium text-slate-300 mb-1">
                      {thisMonth ? 'Tokens renew' : 'Next Renewal'}
                    </p>
                    <p className="text-white">
                      {formatDate((thisMonth?.end.toISOString()) ?? subscription.current_period_end)} ({daysRemaining} days)
                    </p>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <section className="card text-center py-12">
              <CreditCardIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">No Active Subscription</h2>
              <p className="text-slate-400 mb-6">
                Choose a plan to get started with VortexNav.
              </p>
              <Link to="/pricing" className="btn-primary inline-block">
                Choose Plan
              </Link>
            </section>
          )}

          {/* Section B: Token Usage */}
          {subscription && (
            <section className="card">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                  <MapIcon className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {onLedger ? 'Tokens this billing period' : 'Quarterly Token Usage'}
                </h2>
              </div>

              {onLedger && runOut && (
                <TokenAlert
                  percent={usage?.usage_percentage ?? 0}
                  remaining={tokensRemaining}
                  topup={usage?.tokens_topup_remaining ?? 0}
                  runOut={runOut}
                />
              )}

              <UsageBar
                used={tokensUsed}
                total={tokensTotal}
                showDetails={false}
                className="mb-6"
              />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                  <p className="text-sm text-slate-400 mb-1">Used</p>
                  <p className="text-xl font-bold text-white">
                    {tokensUsed.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                  <p className="text-sm text-slate-400 mb-1">Included</p>
                  <p className="text-xl font-bold text-white">
                    {(onLedger ? tokensTotal : tokensIncluded).toLocaleString()}
                  </p>
                </div>
                {onLedger ? (
                  <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                    <p className="text-sm text-slate-400 mb-1">Owed</p>
                    <p className={`text-xl font-bold ${(runOut?.owed ?? 0) > 0 ? 'text-red-400' : 'text-white'}`}>
                      {(runOut?.owed ?? 0).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                    <p className="text-sm text-slate-400 mb-1">Rolled Over</p>
                    <p className="text-xl font-bold text-cyan-400">
                      +{tokensRolledOver.toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                  <p className="text-sm text-slate-400 mb-1">Remaining</p>
                  <p className="text-xl font-bold text-white">
                    {tokensRemaining.toLocaleString()}
                  </p>
                </div>
              </div>

              {usage?.tokens_topup_remaining !== undefined && (usage.package ?? pkg) ? (
                <TopUpCard
                  packs={(usage.package ?? pkg)!.token_packs ?? []}
                  topupRemaining={usage.tokens_topup_remaining}
                  policy={(usage.package ?? pkg)!.out_of_tokens_policy ?? null}
                  amount={(usage.package ?? pkg)!.out_of_tokens_amount ?? null}
                  onBuy={(packId, currency) => buyPackMutation.mutate({ packId, currency })}
                  buying={buyingPack}
                  runOut={runOut}
                  error={buyError}
                />
              ) : (
              <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-400/90">
                  <span className="font-medium text-amber-500">Token Allowance: </span>
                  Tokens are allocated per billing period. When your balance reaches
                  zero, tile downloads will pause. You can upgrade your plan at any
                  time for additional capacity.
                </div>
              </div>
              )}
            </section>
          )}

          <TileUsageCard />

          {/* Section E: Invoice History */}
          <section className="card" id="invoices">
            <div className="flex items-center gap-2 mb-6">
              <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                <DocumentTextIcon className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-white">Invoice History</h2>
            </div>

            {invoicesLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="border border-slate-700 rounded-xl overflow-hidden">
                <InvoiceTable invoices={invoices as any} />
              </div>
            )}

            {invoices.length > 0 && (
              <div className="mt-4 text-center">
                <Link
                  to="/dashboard/invoices"
                  className="text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                >
                  View all invoices
                </Link>
              </div>
            )}
          </section>
        </div>

        {/* Right Column: Payment & Devices */}
        <div className="space-y-8">
          {/* Section D: Payment Methods */}
          {isHostedCheckoutEnabled() ? (
            <section className="card">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                  <CreditCardIcon className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-white">Payment Methods</h2>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Your payment methods are managed securely through Stripe.
              </p>
              <button
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
                className="w-full py-3 px-4 rounded-xl bg-cyan-500 text-white hover:bg-cyan-400 transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50"
              >
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                {portalMutation.isPending ? 'Opening...' : 'Manage Payment Methods'}
              </button>
            </section>
          ) : (
            <section className="card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                    <CreditCardIcon className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-bold text-white">Payment Methods</h2>
                </div>
              </div>

              {methodsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : paymentMethods && paymentMethods.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {paymentMethods.map((method) => (
                    <PaymentMethodCard
                      key={method.id}
                      method={method}
                      onSetDefault={(id) => api.setDefaultPaymentMethod(id).then(() => queryClient.invalidateQueries({ queryKey: ['paymentMethods'] }))}
                      onRemove={(id) => api.deletePaymentMethod(id).then(() => queryClient.invalidateQueries({ queryKey: ['paymentMethods'] }))}
                    />
                  ))}
                </div>
              ) : null}

              <button
                onClick={() => setIsPaymentModalOpen(true)}
                className="w-full py-3 px-4 rounded-xl border border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 hover:bg-slate-800/50 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <PlusIcon className="w-5 h-5" />
                Add Payment Method
              </button>
            </section>
          )}

          {/* Section C: Active Devices */}
          <section className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                  <DevicePhoneMobileIcon className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-bold text-white">Active Devices</h2>
              </div>
              <span className="bg-slate-700 text-white text-sm font-bold px-2.5 py-1 rounded-full">
                {deviceCount}
              </span>
            </div>

            <p className="text-sm text-slate-400 mb-4">
              {deviceCount > 0
                ? `You have ${deviceCount} device${deviceCount !== 1 ? 's' : ''} linked to this subscription.`
                : 'No devices linked to your subscription yet.'}
            </p>

            <Link to="/dashboard/devices" className="block w-full btn-secondary text-sm text-center">
              Manage Devices
            </Link>
          </section>

          {/* Section F: Support */}
          <section className="card bg-slate-800/30 border-slate-700/50">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-slate-700 text-slate-300 shrink-0">
                <QuestionMarkCircleIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-medium mb-1">
                  Need help with billing?
                </h3>
                <p className="text-sm text-slate-400 mb-3">
                  Contact our support team for questions about your plan,
                  invoices, or usage.
                </p>
                <a
                  href="mailto:billing@vortexmarine.com"
                  className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  billing@vortexmarine.com
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Modals */}
      <PlanComparisonModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        currentPackageId={subscription?.package_id || ''}
        packages={packages || []}
        isChanging={isPlanChanging}
        onSelectPlan={handleSelectPlan}
        onLedger={onLedger}
      />

      <AddPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={handlePaymentMethodAdded}
      />

      {isCancelModalOpen && subscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setIsCancelModalOpen(false)}
          />
          <div className="relative w-full max-w-md bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">Cancel Subscription?</h2>
              <button
                onClick={() => setIsCancelModalOpen(false)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-400">
                  Your subscription will remain active until{' '}
                  <span className="text-white font-medium">
                    {formatDate(subscription.current_period_end)}
                  </span>
                  . You can reactivate at any time before then.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-700 flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => setIsCancelModalOpen(false)}
                className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
              >
                Don't Cancel
              </button>
              <button
                onClick={() => {
                  cancelMutation.mutate(undefined, {
                    onSuccess: () => setIsCancelModalOpen(false),
                  });
                }}
                disabled={cancelMutation.isPending}
                className="flex-1 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl font-medium border border-red-500/30 transition-colors disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BillingPlanPage;
